import { useEffect, useRef } from 'react';
import { useStyles, Flex, Button, pxToRem, Grid } from '@archipro-design/aria';
import { InPageSearchBar } from '@modules/search/components/in-page-search-bar/InPageSearchBar';
import * as S from './SearchFilter.style';
import { useProfessionalTabSearch } from '~/modules/professional/hook/use-professional-tab-search';
import { useCurrentTabPath } from '~/modules/professional/hook/use-current-tab-path';
import SearchSort from '~/modules/search/components/search-filter-section/RoccoBasedSearchSort';
import { Filter } from '@archipro-design/icons';
import { applyCount } from '@modules/search/components/filter-drawer/FilterDrawer';
import FilterBarFilters from '~/modules/search/components/search-filter-section/FilterBarFilters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { useSearchSortOptions } from '~/modules/search/hook/use-search-sort-options';
import { useSearchSorts } from '~/modules/search/hook/use-search-sorts';
import type { SearchFilterProp } from './SearchFilter';
import type { OptionResult } from '@modules/search/components/search-filter-section/RoccoBasedSearchSort';

export const SearchFilterDesktop: React.FC<SearchFilterProp> = ({
    searchFilters,
    toggleFilterDrawer,
    filterDrawerOpen,
    disableSort,
    disableToggleFilter,
}) => {
    const currentTabPath = useCurrentTabPath();
    const { css } = useStyles({ currentTabPath });

    const selectedFilterButtonRef = useRef<HTMLDivElement>(null);

    const tabSearch = useProfessionalTabSearch();

    const category = useMatchesHandleData<SearchIndex>('category', 'project');

    const isAllowFilters = useMatchesHandleData<boolean>('allowFilters', true);

    const { appliedFilters, filterBarFilters, updateFilter } = searchFilters;

    const dropdownOptionResult = useSearchSortOptions({ category });

    const { sortOptions } = useSearchSorts({
        category,
    });

    useEffect(() => {
        if (!selectedFilterButtonRef.current) return;
        selectedFilterButtonRef.current.scrollIntoView({
            inline: 'center',
            block: 'center',
        });
    }, [sortOptions]);

    return (
        <div className={css(S.DesktopFilterWarp)}>
            <Flex space={'between'}>
                <div className={css(S.SearchBarWarp)}>
                    <InPageSearchBar {...tabSearch} />
                </div>
                <Grid
                    className={css(S.DesktopFilterGroup)}
                    columns={
                        disableToggleFilter
                            ? 'repeat(2, auto)'
                            : 'repeat(3, auto)'
                    }
                    rows={1}
                >
                    <div>
                        {isAllowFilters && (
                            <Flex
                                className={css(S.FilterButtonsWarp)}
                                vAlign={'center'}
                            >
                                {filterBarFilters &&
                                    filterBarFilters.length > 0 && (
                                        <FilterBarFilters
                                            filters={filterBarFilters}
                                            updateFilter={updateFilter}
                                        />
                                    )}
                            </Flex>
                        )}
                    </div>

                    {isAllowFilters && !disableToggleFilter && (
                        <Flex
                            className={css(S.FilterButtonsWarp)}
                            vAlign={'center'}
                        >
                            <Button
                                transparent
                                size={18}
                                variables={{
                                    buttonPadding: 0,
                                    iconPadding: 0,
                                    buttonSize18IconXMargin: pxToRem(12),
                                }}
                                disabled={disableSort}
                                iconBefore={<Filter />}
                                onClick={toggleFilterDrawer}
                                styles={{
                                    fontWeight: 500,
                                }}
                            >
                                {applyCount(
                                    `${
                                        filterDrawerOpen ? 'Hide' : 'Show'
                                    } Filters`,
                                    appliedFilters?.length ?? 0
                                )}
                            </Button>
                        </Flex>
                    )}

                    <div className={css(S.SortButtonWarp)}>
                        {!disableSort &&
                            dropdownOptionResult.items.length > 0 && (
                                <SearchSort
                                    optionResult={
                                        dropdownOptionResult as OptionResult
                                    }
                                />
                            )}
                    </div>
                </Grid>
            </Flex>
        </div>
    );
};
